import { useMemo, useRef } from 'react';
import { ValidationLevel, ValidationProps } from 'ui';
import { routeConstants } from 'app/routes/route-constants';
import { isDefinedIncludingZero } from 'app/infrastructure/data/number';
import { Configuration } from 'app/components/metro-line/types';
import { SavedReportsValidation } from 'app/area/shared/saved-reports/types-store';
import { DefaultFilterValidation } from 'app/area/shared/default-filters/types-store';
import { SavedReportDashboard } from '../../common/types-store';
import { DashboardAdminTranslation } from '../../constants';
import { DashboardItemValidation } from '../types-store';
import { DashboardSectionType } from './types';

const isValid = (validationProps: ValidationProps[], level: ValidationLevel) =>
  !validationProps.some(prop => (prop?.messages?.length ?? 0) > 0 && prop?.level === level);

export const getDashboardRoute = (
  dashboardId: number,
  favouriteId: number,
  section?: DashboardSectionType
) => isDefinedIncludingZero(dashboardId)
    ? routeConstants.dashboardAdminItemFor(dashboardId, favouriteId, section)
    : routeConstants.dashboardAdminAddFor(section);

export const dashboardConfiguration = (
  dashboardId: number,
  favouriteId: number,
  validationErrors: DashboardItemValidation,
  savedReportsValidation: SavedReportsValidation<SavedReportDashboard>,
  filterValidation: { [domainId: number]: DefaultFilterValidation }
): Configuration<DashboardSectionType>[] => {

  const detailsSectionRef = useRef<HTMLDivElement | null>(null);
  const visibilitySectionRef = useRef<HTMLDivElement | null>(null);
  const savedReportsSectionRef = useRef<HTMLDivElement | null>(null);
  const filtersSectionRef = useRef<HTMLDivElement | null>(null);
  const translationsSectionRef = useRef<HTMLDivElement | null>(null);

  return useMemo(() => {

    const getSectionRoute = (section: DashboardSectionType) => getDashboardRoute(dashboardId, favouriteId, section);

    const renderLanguageOnTop = !isDefinedIncludingZero(dashboardId);
    const languageSection = {
      section: DashboardSectionType.Translations,
      visible: true,
      url: getSectionRoute(DashboardSectionType.Translations),
      iconUrl: '/i/case-sections/actions.svg',
      label: (translation: DashboardAdminTranslation) => translation.descriptions_and_translations,
      ref: translationsSectionRef,
      isValid: isValid([validationErrors.language], ValidationLevel.error)
    };

    const savedReportValues = Object.values(savedReportsValidation ?? {});

    return [
      ...(renderLanguageOnTop ? [languageSection] : []),
      {
        section: DashboardSectionType.Details,
        visible: true,
        iconUrl: '/i/case-sections/default.svg',
        url: getSectionRoute(DashboardSectionType.Details),
        label: (translation: DashboardAdminTranslation) => translation.details,
        ref: detailsSectionRef,
        isValid: isValid([validationErrors.sequence, validationErrors.disabled, validationErrors.accessType], ValidationLevel.error)
      },
      {
        section: DashboardSectionType.Visibility,
        visible: true,
        iconUrl: '/i/case-sections/where-and-what.svg',
        url: getSectionRoute(DashboardSectionType.Visibility),
        label: (translation: DashboardAdminTranslation) => translation.visibility,
        ref: visibilitySectionRef,
        isValid: isValid([validationErrors.subset, validationErrors.application, validationErrors.person], ValidationLevel.error)
      },
      {
        section: DashboardSectionType.SavedReports,
        visible: true,
        iconUrl: '/i/case-sections/connected-cases.svg',
        url: getSectionRoute(DashboardSectionType.SavedReports),
        label: (translation: DashboardAdminTranslation) => translation.saved_reports,
        ref: savedReportsSectionRef,
        isValid: savedReportValues.every(savedReport => isValid(Object.values(savedReport), ValidationLevel.error)),
        hasWarnings: savedReportValues.some(savedReport => !isValid(Object.values(savedReport), ValidationLevel.warning))
      },
      {
        section: DashboardSectionType.Filters,
        visible: true,
        iconUrl: '/i/funnel.svg',
        url: getSectionRoute(DashboardSectionType.Filters),
        label: (translation: DashboardAdminTranslation) => translation.filters,
        ref: filtersSectionRef,
        isValid: Object.values(filterValidation ?? {}).every(filter => isValid(Object.values(filter), ValidationLevel.error))
      },
      ...(!renderLanguageOnTop ? [languageSection] : [])
    ];
  }, [dashboardId, favouriteId, validationErrors, savedReportsValidation,
    detailsSectionRef, visibilitySectionRef, savedReportsSectionRef, filtersSectionRef, translationsSectionRef]);
};
